import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import {
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  Card,
  Divider,
  Paper,
  Input,
  IconButton,
  Select,
  MenuItem,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import DeleteIcon from "@mui/icons-material/Delete";
import { useForm, useFieldArray, Controller, useWatch } from "react-hook-form";

// Component: EventDetailItem
const EventDetailItem = ({ index, control, setValue, remove, handleDetailImageUpload }) => {
  const detailType = useWatch({
    control,
    name: `details[${index}].type`,
  });

  return (
    <Grid item xs={12}>
      <Grid container spacing={2}>
        {/* Detail Type */}
        <Grid item xs={3}>
          <Controller
            name={`details[${index}].type`}
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                fullWidth
                variant="outlined"
                onChange={(e) => {
                  field.onChange(e);
                  if (e.target.value === "image") {
                    setValue(`details[${index}].content`, "");
                  }
                }}
              >
                <MenuItem value="description">Description</MenuItem>
                <MenuItem value="image">Image</MenuItem>
              </Select>
            )}
          />
        </Grid>

        {/* Detail Content */}
        <Grid item xs={7}>
          {detailType === "image" ? (
            <Paper variant="outlined" sx={{ p: 2, textAlign: "center" }}>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => handleDetailImageUpload(index, e)}
                sx={{ display: "none" }}
                id={`detail-image-upload-${index}`}
              />
              <label htmlFor={`detail-image-upload-${index}`}>
                <IconButton color="primary" component="span">
                  <AddPhotoAlternateIcon fontSize="large" />
                </IconButton>
              </label>
              <Controller
                name={`details[${index}].content`}
                control={control}
                render={({ field }) =>
                  field.value ? <img src={field.value} alt="Detail" height="120" style={{ marginTop: 8 }} /> : null
                }
              />
            </Paper>
          ) : (
            <Controller
              name={`details[${index}].content`}
              control={control}
              render={({ field }) => (
                <TextField {...field} placeholder="Enter description" multiline fullWidth minRows={4} variant="outlined" />
              )}
            />
          )}
        </Grid>

        {/* Remove Button */}
        <Grid item xs={2}>
          <IconButton color="error" onClick={() => remove(index)}>
            <DeleteIcon />
          </IconButton>
        </Grid>
      </Grid>
    </Grid>
  );
};

// Component: EventDetailsSection
const EventDetailsSection = ({ fields, control, setValue, remove, append, handleDetailImageUpload }) => (
  <Grid item xs={12}>
    <Card sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" fontWeight="bold">Event Details</Typography>
      <Divider sx={{ my: 2 }} />
      <Grid container spacing={2}>
        {fields.map((item, index) => (
          <EventDetailItem
            key={item.id}
            index={index}
            control={control}
            setValue={setValue}
            remove={remove}
            handleDetailImageUpload={handleDetailImageUpload}
          />
        ))}
      </Grid>
    </Card>
  </Grid>
);

// Main Component: UpdateEvent
const UpdateEvent = () => {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [eventImage, setEventImage] = useState(null);

  const { control, setValue, handleSubmit, reset } = useForm({
    defaultValues: {
      name: "",
      start_date: "",
      end_date: "",
      details: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "details",
  });

  useEffect(() => {
    const fetchEventData = async () => {
      try {
        const eventResponse = await axios.get(`http://localhost/CMS/backend/script/bodyfriend/event/get_event.php?id=${eventId}`);
        if (eventResponse.data.success && eventResponse.data.event) {
          setEvent(eventResponse.data.event);
          setValue("name", eventResponse.data.event.name);
          setValue("start_date", eventResponse.data.event.start_date);
          setValue("end_date", eventResponse.data.event.end_date);
          setEventImage(eventResponse.data.event.image ? `http://localhost/CMS/backend/uploads/bodyfriend/eventImages/${eventResponse.data.event.image}` : null);
        } else {
          setError("Event not found.");
        }

        const eventDetailsResponse = await axios.get(`http://localhost/CMS/backend/script/bodyfriend/event/get_event_details.php?id=${eventId}`);
        if (eventDetailsResponse.data.success && eventDetailsResponse.data.event_details) {
          reset((formValues) => ({
            ...formValues,
            details: eventDetailsResponse.data.event_details.map((detail) => ({ type: detail.type, content: detail.content })),
          }));
        }
      } catch (err) {
        setError("Error fetching event data.");
      } finally {
        setLoading(false);
      }
    };

    if (eventId) {
      fetchEventData();
    }
  }, [eventId, setValue, append, reset]);

  const onSubmit = (data) => {
    console.log("Updated Event Data:", data);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
    }
  };

  const handleDetailImageUpload = (index, e) => {
    const file = e.target.files[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setValue(`details[${index}].content`, imageUrl);
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;
  if (!event) return <div>No event found.</div>;

  return (
    <Container sx={{ padding: 4 }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>Edit Event</Typography>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={4} direction="column">
          <Grid item xs={12}>
            <Card sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" fontWeight="bold">Event</Typography>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Controller name="name" control={control} render={({ field }) => <TextField {...field} label="Event Name" fullWidth variant="outlined" />} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller name="start_date" control={control} render={({ field }) => <TextField {...field} label="Start Date" fullWidth variant="outlined" type="date" />} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller name="end_date" control={control} render={({ field }) => <TextField {...field} label="End Date" fullWidth variant="outlined" type="date" />} />
                </Grid>
              </Grid>
              
              {/* Event Image Section */}
              <Grid item xs={12}>
                <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
                  Event Image (*Image Only)
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, textAlign: "center" }}>
                  {eventImage ? (
                    <img
                      src={eventImage} // Show the original image if no new image is selected
                      alt="Event"
                      height="120"
                      style={{ marginTop: 8 }}
                    />
                  ) : (
                    <p>No image available for this event.</p>
                  )}
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    sx={{ marginTop: 2 }}
                  />
                </Paper>
              </Grid>
            </Card>
          </Grid>



          <EventDetailsSection fields={fields} control={control} setValue={setValue} remove={remove} append={append} handleDetailImageUpload={handleDetailImageUpload} />

          <Grid item xs={12} sx={{ textAlign: "center" }}>
            <Button variant="contained" color="primary" type="submit" startIcon={<SaveIcon />}>Save Changes</Button>
          </Grid>
        </Grid>
      </form>
    </Container>
  );
};

export default UpdateEvent;
